import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard, Select, Button, Modal } from "@/components/ui";
import { toast } from "sonner";
import { FileWarning, CheckCircle2, Clock, TrendingUp } from "lucide-react";

export default function CareGaps() {
  const [gaps, setGaps] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [closing, setClosing] = useState(null);
  const [reason, setReason] = useState("Service completed / documented");

  const load = () => {
    api.get("/care-gaps", { params: { status } }).then((r) => setGaps(r.data));
    api.get("/care-gaps-summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const setGapStatus = async (id, newStatus, closed_reason) => {
    await api.patch(`/care-gaps/${id}`, { status: newStatus, closed_reason });
    toast.success(`Gap ${newStatus.toLowerCase()}`);
    setClosing(null); load();
  };

  if (!summary) return <Spinner />;

  return (
    <div className="vc-fade-up">
      <PageHeader title="Care Gap Closure" subtitle="Identify, work, and close quality/HEDIS care gaps across your population." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Open Gaps" value={summary.open} icon={FileWarning} tone="red" />
        <StatCard label="In Progress" value={summary.in_progress} icon={Clock} tone="amber" />
        <StatCard label="Closed" value={summary.closed} icon={CheckCircle2} tone="brand" />
        <StatCard label="Closure Rate" value={`${summary.closure_rate}%`} icon={TrendingUp} tone="blue" />
      </div>

      <Card className="p-4 mb-4">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="gaps-filter">
          <option value="">All Statuses</option><option>Open</option><option>In Progress</option><option>Closed</option>
        </Select>
      </Card>

      {gaps === null ? <Spinner /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Care Gap</th><th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Measure</th><th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Action</th></tr></thead>
              <tbody className="divide-y divide-border">
                {gaps.map((g) => (
                  <tr key={g.id} className="hover:bg-muted transition-colors" data-testid="gap-row">
                    <td className="px-4 py-3 font-medium">{g.description}</td>
                    <td className="px-4 py-3 text-muted-foreground">{g.member_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{g.measure || "—"}</td>
                    <td className="px-4 py-3"><Badge tone={g.priority === "High" ? "High" : g.priority === "Low" ? "Low" : "Medium"}>{g.priority}</Badge></td>
                    <td className="px-4 py-3"><Badge tone={g.status === "Closed" ? "Completed" : g.status === "In Progress" ? "In Progress" : "Open"}>{g.status}</Badge></td>
                    <td className="px-4 py-3">
                      {g.status !== "Closed" ? (
                        <div className="flex gap-2">
                          {g.status === "Open" && <Button size="sm" variant="outline" onClick={() => setGapStatus(g.id, "In Progress")} data-testid={`gap-start-${g.id}`}>Start</Button>}
                          <Button size="sm" onClick={() => { setClosing(g); setReason("Service completed / documented"); }} data-testid={`gap-close-${g.id}`}>Close</Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">{g.closed_reason} · {g.closed_by}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={!!closing} onClose={() => setClosing(null)} title="Close Care Gap">
        {closing && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{closing.description} — <span className="font-medium text-foreground">{closing.member_name}</span></p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Closure Reason</label>
              <Select className="w-full mt-1.5" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="gap-close-reason">
                {["Service completed / documented", "Member declined", "Contraindicated / exclusion", "Completed by external provider", "Resolved via outreach"].map((r) => <option key={r}>{r}</option>)}
              </Select>
            </div>
            <Button className="w-full" onClick={() => setGapStatus(closing.id, "Closed", reason)} data-testid="gap-close-confirm">Confirm Closure</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
